import React, { useState } from 'react';
import styles from './AuthCard.module.css';
import { API } from '../config';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheckCircle, FaExclamationCircle, FaMoon, FaSun, FaSpinner } from 'react-icons/fa';
import TypyingText from './includes/TypingText';
import logo from '../assets/img/Logo_light.png';

const AuthCard = ({ onLogin }) => {
  const [flipped, setFlipped] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  const [registerForm, setRegisterForm] = useState({ email: '', password: '', confirm: '' });
  const [loginForm, setLoginForm]       = useState({ email: '', password: '' });

  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const [forgotPw,       setForgotPw]       = useState(false);
  const [forgotStep,     setForgotStep]     = useState(1);
  const [forgotEmail,    setForgotEmail]    = useState('');
  const [forgotPassword, setForgotPassword] = useState('');
  const [forgotConfirm,  setForgotConfirm]  = useState('');
  const [forgotLoading,  setForgotLoading]  = useState(false);
  const EMPLOYEE_ROLES = ['Full_Time', 'Part_Time', 'Intern', 'Trainer', 'Freelance', 'employee'];

  const navigate = useNavigate();

  const handleChange = (formType, field, value) => {
    if (formType === 'register') {
      setRegisterForm({ ...registerForm, [field]: value });
    } else {
      setLoginForm({ ...loginForm, [field]: value });
    }
    setError('');
    setSuccess('');
  };

  // =====================================================
  // ✅ Register
  // =====================================================
// Replace your handleRegister function with this:
const handleRegister = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError('');
  setSuccess('');

  if (registerForm.password !== registerForm.confirm) {
    setError("Passwords do not match.");
    setLoading(false);
    return;
  }

  try {
    const res = await fetch(API.REGISTER, {
      method: "POST",
      headers: { 
        'Content-Type': 'application/json' // Tell PHP you're sending JSON
      },
      body: JSON.stringify({
        email: registerForm.email.trim(),
        password: registerForm.password,
        confirm_password: registerForm.confirm, // Match the PHP key name
      }),
    });

    const data = await res.json();
    setLoading(false);

    if (data.success) {
      setSuccess(data.message || "Registered successfully!");
      setRegisterForm({ email: '', password: '', confirm: '' });
      setTimeout(() => setFlipped(false), 1500);
    } else {
      setError(data.message || "Registration failed.");
    }
  } catch (err) {
    setLoading(false);
    setError("Network error. Please try again.");
  }
};

  // =====================================================
  // ✅ Login
  // =====================================================
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await fetch(API.LOGIN, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email:    loginForm.email.trim(),
          password: loginForm.password,
        }),
      });

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { success: false, message: "Server error. Please try again." };
      }

      setLoading(false);

      if (data.success) {
        setSuccess("Login successful!");
        const user = data.user;

        localStorage.setItem("person_id", user.person_id);
        localStorage.setItem("role",      user.role);
        localStorage.setItem("user_id",   user.id);
        localStorage.setItem("email",     user.email);

        setTimeout(() => {
          onLogin(user);
          if (user.role === "admin") {
            navigate("/admin-dashboard");
          } else if (EMPLOYEE_ROLES.includes(user.role)){
            navigate("/employee-profile");
          } else if (user.role === "student") {
            navigate("/student-dashboard");
          } else {
            navigate("/home");
          }
        }, 800);

      } else {
        setError(data.message || "Incorrect email or password. Please try again.");
      }

    } catch (err) {
      setLoading(false);
      setError("Incorrect email or password. Please try again.");
    }
  };

  // =====================================================
  // Forgot Password — Step 1
  // =====================================================
  const handleForgotEmail = async (e) => {
    e.preventDefault();
    setForgotLoading(true); setError(''); setSuccess('');
    try {
      const res  = await fetch(API.FORGOT_PASSWORD, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });

      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { success: false, message: "Server error." }; }

      setForgotLoading(false);
      if (data.success) {
        setForgotStep(2);
        setSuccess(data.message);
      } else {
        setError(data.message || "Email not found. Please try again.");
      }
    } catch {
      setForgotLoading(false);
      setError("Network error. Please try again.");
    }
  };

  // =====================================================
  // Forgot Password — Step 2
  // =====================================================
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true); setError(''); setSuccess('');

    if (!forgotPassword || !forgotConfirm) {
      setForgotLoading(false);
      setError("Both password fields are required.");
      return;
    }
    if (forgotPassword !== forgotConfirm) {
      setForgotLoading(false);
      setError("Passwords do not match.");
      return;
    }

    try {
      const res  = await fetch(API.FORGOT_PASSWORD, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email:            forgotEmail,
          new_password:     forgotPassword,
          confirm_password: forgotConfirm,
        }),
      });

      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { success: false, message: "Server error." }; }

      setForgotLoading(false);
      if (data.success) {
        setSuccess(data.message);
        setForgotEmail(''); setForgotPassword(''); setForgotConfirm('');
        setForgotStep(1); setForgotPw(false);
      } else {
        setError(data.message || "Password update failed. Please try again.");
      }
    } catch {
      setForgotLoading(false);
      setError("Network error. Please try again.");
    }
  };

  const buttonVariants = {
    rest:    { scale: 1,    backgroundColor: 'var(--primary-blue)' },
    hover:   { scale: 1.05, backgroundColor: 'var(--accent-orange)' },
    tap:     { scale: 0.95 },
    loading: { backgroundColor: '#6b7280', transition: { duration: 0.3 } },
  };

  return (
    <div className={`${styles.mainContainer} ${darkMode ? styles.dark : ''}`}>
      <div className={styles.toggleDark}>
        <span onClick={() => setDarkMode(!darkMode)}>
          {darkMode ? <FaSun size={18} /> : <FaMoon size={18} />}
        </span>
      </div>

      <div className={styles.bannerSection}>
        <TypyingText />
      </div>

      <div className={styles.authContainer}>
        <motion.div
          className={`${styles.card} ${flipped ? styles.flipped : ''}`}
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >

          {/* ======================== REGISTER ======================== */}
          <div className={styles.back}>
            <div className={styles.logoContainer}>
              <img src={logo} alt="Logo" className={styles.logo} />
            </div>
            <motion.h2 initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
              Register
            </motion.h2>
            <form onSubmit={handleRegister}>
              <input
                type="email"
                placeholder="Email"
                value={registerForm.email}
                onChange={(e) => handleChange('register', 'email', e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={registerForm.password}
                onChange={(e) => handleChange('register', 'password', e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="Confirm Password"
                value={registerForm.confirm}
                onChange={(e) => handleChange('register', 'confirm', e.target.value)}
                required
              />

              <AnimatePresence>
                {error && (
                  <motion.div className={styles.error} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                    <FaExclamationCircle /> {error}
                  </motion.div>
                )}
                {success && (
                  <motion.div className={styles.success} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                    <FaCheckCircle /> {success}
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.button
                type="submit" disabled={loading}
                variants={buttonVariants} initial="rest" whileHover="hover" whileTap="tap"
                animate={loading ? "loading" : "rest"} className={styles.submitBtn}
              >
                {loading ? <motion.span animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }}><FaSpinner /></motion.span> : 'Register'}
              </motion.button>

              <p style={{ marginTop: '15px', fontSize: '14px' }}>
                Already have an account?{' '}
                <motion.span className={styles.linkText} onClick={() => { setFlipped(false); setSuccess(''); setError(''); }} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                  Log in
                </motion.span>
              </p>
            </form>
          </div>

          {/* ======================== LOGIN & FORGOT ======================== */}
          <div className={styles.front}>
            <div className={styles.logoContainer}>
              <img src={logo} alt="Logo" className={styles.logo} />
            </div>

            {!forgotPw ? (
              <>
                <motion.h2 initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>Login</motion.h2>
                <form onSubmit={handleLogin}>
                  <input
                    type="email" placeholder="Email"
                    value={loginForm.email}
                    onChange={(e) => handleChange('login', 'email', e.target.value)}
                    required
                  />
                  <input
                    type="password" placeholder="Password"
                    value={loginForm.password}
                    onChange={(e) => handleChange('login', 'password', e.target.value)}
                    required
                  />

                  <AnimatePresence>
                    {error && (
                      <motion.div className={styles.error} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                        <FaExclamationCircle /> {error}
                      </motion.div>
                    )}
                    {success && (
                      <motion.div className={styles.success} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                        <FaCheckCircle /> {success}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.button
                    type="submit" disabled={loading}
                    variants={buttonVariants} initial="rest" whileHover="hover" whileTap="tap"
                    animate={loading ? "loading" : "rest"} className={styles.submitBtn}
                  >
                    {loading ? <motion.span animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }}><FaSpinner /></motion.span> : 'Login'}
                  </motion.button>

                  <div className="d-flex justify-content-between align-items-center" style={{ marginTop: '10px' }}>
                    <p style={{ fontSize: '14px', margin: 0 }}>
                      Don't have an account?{' '}
                      <motion.span className={styles.linkText} onClick={() => { setFlipped(true); setSuccess(''); setError(''); }} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                        Register
                      </motion.span>
                    </p>
                    <motion.span
                      className={styles.linkText}
                      style={{ fontSize: '14px', cursor: 'pointer', textDecoration: 'underline', marginLeft: '16px' }}
                      onClick={() => { setForgotPw(true); setForgotStep(1); setError(''); setSuccess(''); }}
                      whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    >
                      Forgot Password?
                    </motion.span>
                  </div>
                </form>
              </>
            ) : (

              <div className={styles.forgotContainer}>
                <motion.h2 initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>Forgot Password</motion.h2>

                {forgotStep === 1 ? (
                  <form onSubmit={handleForgotEmail}>
                    <input
                      type="email" placeholder="Enter your registered email"
                      value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required
                    />
                    <motion.button type="submit" disabled={forgotLoading} className={styles.submitBtn}
                      variants={buttonVariants} initial="rest" whileHover="hover" whileTap="tap" animate={forgotLoading ? "loading" : "rest"}>
                      {forgotLoading ? <FaSpinner className="fa-spin" /> : 'Next'}
                    </motion.button>
                    <p style={{ marginTop: '15px', fontSize: '14px' }}>
                      <motion.span className={styles.linkText} style={{ cursor: 'pointer' }} onClick={() => { setForgotPw(false); setForgotStep(1); setError(''); setSuccess(''); }} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                        Back to Login
                      </motion.span>
                    </p>
                  </form>
                ) : (
                  <form onSubmit={handleForgotPassword}>
                    <input type="password" placeholder="New Password" value={forgotPassword} onChange={e => setForgotPassword(e.target.value)} required />
                    <input type="password" placeholder="Confirm New Password" value={forgotConfirm} onChange={e => setForgotConfirm(e.target.value)} required />
                    <motion.button type="submit" disabled={forgotLoading} className={styles.submitBtn}
                      variants={buttonVariants} initial="rest" whileHover="hover" whileTap="tap" animate={forgotLoading ? "loading" : "rest"}>
                      {forgotLoading ? <FaSpinner className="fa-spin" /> : 'Update Password'}
                    </motion.button>
                    <p style={{ marginTop: '15px', fontSize: '14px' }}>
                      <motion.span className={styles.linkText} style={{ cursor: 'pointer' }} onClick={() => { setForgotPw(false); setForgotStep(1); setError(''); setSuccess(''); }} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                        Back to Login
                      </motion.span>
                    </p>
                  </form>
                )}

                <AnimatePresence>
                  {error && (
                    <motion.div className={styles.error} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                      <FaExclamationCircle /> {error}
                    </motion.div>
                  )}
                  {success && (
                    <motion.div className={styles.success} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                      <FaCheckCircle /> {success}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>

        </motion.div>
      </div>
    </div>
  );
};

export default AuthCard;